import type {
  BridgePersona,
  ThemeId,
  AspirationId,
  BridgeInterpretation,
} from "./types";
import {
  THEMES,
  ASPIRATIONS,
  INTERPRETATION_DISCLAIMER,
} from "./options";
import {
  THEME_MEANINGS,
  ARCHETYPE_INSIGHTS,
} from "./banks";
import { fnv1a } from "@/lib/infiniteSelves/scoring";

export { INTERPRETATION_DISCLAIMER };

export interface BuildInterpretationInput {
  persona: BridgePersona;
  themes: ThemeId[];
  aspirations: AspirationId[];
}

/**
 * Build a tentative, educational interpretation of an imagined identity.
 *
 * Rules:
 * - Purely a POSSIBLE reading based solely on chosen themes, aspirations, and the self's free-sample traits.
 * - Always phrased tentatively ("may", "might", "could suggest").
 * - Clear caveat stating the visitor is the authority and can reword or reject it.
 * - Deterministic, pure hash id.
 */
export function buildInterpretation(input: BuildInterpretationInput): BridgeInterpretation {
  const { persona, themes, aspirations } = input;

  // Stable ID
  const themesKey = [...themes].sort().join(",");
  const aspKey = [...aspirations].sort().join(",");
  const personaKey = persona.kind === "self" ? `${persona.archetype}:${persona.name}` : "own";
  const rawId = fnv1a(`interp:${personaKey}:${themesKey}:${aspKey}`);
  const id = `interp-${(rawId >>> 0).toString(16).slice(0, 8)}`;

  // Find theme labels & meanings
  const themeMeanings = themes
    .map((t) => THEME_MEANINGS[t])
    .filter(Boolean);

  const themeLabels = themes
    .map((t) => THEMES.find((opt) => opt.id === t)?.label)
    .filter(Boolean) as string[];

  // Find aspiration labels
  const aspirationLabels = aspirations
    .map((a) => ASPIRATIONS.find((opt) => opt.id === a)?.label)
    .filter(Boolean) as string[];

  // Assemble opening observation
  let opening = "";
  if (persona.kind === "self") {
    opening = `When you reflect on ${persona.name}, living as ${persona.occupation.toLowerCase()} in "${persona.scenarioLabel}", this imagined identity may be expressing an authentic inner pull toward ${
      themeMeanings.length > 0 ? themeMeanings.join(", and ") : "unexplored dimensions of yourself"
    }.`;
  } else {
    opening = `When you explore your imagined self, your focus on ${
      themeLabels.length > 0 ? themeLabels.join(" and ") : "these themes"
    } suggests that your daydreams may be pointing toward ${
      themeMeanings.length > 0 ? themeMeanings.join(", and ") : "qualities you are ready to bring forward"
    }.`;
  }

  // Construct bullet points
  const bullets: string[] = [];

  // 1. Theme insight
  if (themeLabels.length > 0) {
    const joinedThemes = themeLabels.join(", ");
    bullets.push(
      `Core Values: Your focus on ${joinedThemes} might indicate that you are seeking more autonomy, creative energy, or depth in your waking schedule.`
    );
  }

  // 2. Aspiration bridge
  if (aspirationLabels.length > 0) {
    const joinedAspirations = aspirationLabels.join(", ");
    bullets.push(
      `Real-world Anchor: Grounding these values in ${joinedAspirations} can offer practical channels to experience that same energy without needing a fantasy world.`
    );
  }

  // 3. Persona / Archetype reflection (if chosen)
  if (persona.kind === "self") {
    bullets.push(ARCHETYPE_INSIGHTS[persona.archetype]);
  } else {
    bullets.push(
      "Personal Sandbox: Your private daydreams act as a creative laboratory where you can test out feelings, boundaries, and ambitions before trying them in everyday life."
    );
  }

  // Summary paragraph
  const text = `${opening} Rather than viewing this fantasy as an unattainable escape or a diversion, it might be helpful to view it as a compass: what if the essence of this imagined life can be translated into small, achievable steps right where you are?`;

  return {
    id,
    text,
    bullets,
    caveat: INTERPRETATION_DISCLAIMER,
  };
}
